import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Paper,
  Typography,
  CircularProgress,
  Avatar,
  Link,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ManualUtilizare from './ManualUtilizare';

// API URL pentru chatbot (server de producție)
const CHATBOT_API_URL = 'https://chat.ilegis.ro/api/chat';

const AsistentChat = () => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [openManual, setOpenManual] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const handleOpenManual = () => setOpenManual(true);
  const handleCloseManual = () => setOpenManual(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Mesaj de bun venit
    setMessages([
      {
        id: 1,
        text: 'Bună! Sunt Asistentul JuristPRO NEXT. Pot să te ajut cu întrebări despre utilizarea aplicației. Cu ce te pot ajuta astăzi?',
        sender: 'bot',
        timestamp: new Date(),
      },
    ]);
    
    // Focus pe input când se montează componenta (când se deschide drawer-ul)
    setTimeout(() => {
      inputRef.current?.focus();
    }, 300);
  }, []);

  const formatMarkdown = (text) => {
    if (!text) return '';
    
    return text
      // Convertește imagini Markdown: ![alt](url) -> <img>
      .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" style="max-width: 100%; height: auto; border-radius: 8px; margin: 10px 0; cursor: zoom-in;" />')
      // Link-uri Markdown: [text](url) -> <a>
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" style="color: #14B9D6; text-decoration: none; border-bottom: 1px solid #14B9D6;">$1</a>')
      // **bold**
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      // New lines
      .replace(/\n/g, '<br>');
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage = {
      id: Date.now(),
      text: inputMessage,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await fetch(CHATBOT_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: inputMessage,
          conversation_history: [],
          collection: 'juristpro_manual',  // Specifică colecția JuristPRO
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const botMessage = {
          id: Date.now() + 1,
          text: data.response || 'Ne pare rău, nu am putut procesa cererea.',
          html: data.html || '',
          sources: data.sources || [],
          sender: 'bot',
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, botMessage]);
      } else {
        throw new Error('Eroare la comunicarea cu serverul');
      }
    } catch (error) {
      console.error('Eroare:', error);
      const errorMessage = {
        id: Date.now() + 1,
        text: 'Ne pare rău, nu pot accesa serverul de asistență. Asigură-te că serverul chatbot rulează pe portul 8765.',
        sender: 'bot',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      // Refocusează input-ul după ce se termină loading-ul
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('ro-RO', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 200px)',
        backgroundColor: '#f5f5f5',
        borderRadius: '8px',
        overflow: 'hidden',
      }}
    >
      {/* Zona de mesaje */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        {messages.map((message) => (
          <Box
            key={message.id}
            sx={{
              display: 'flex',
              justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
              alignItems: 'flex-start',
              gap: '12px',
              animation: 'slideInMessage 0.4s ease-out',
              '@keyframes slideInMessage': {
                '0%': {
                  opacity: 0,
                  transform: message.sender === 'user' ? 'translateX(20px)' : 'translateX(-20px)',
                },
                '100%': {
                  opacity: 1,
                  transform: 'translateX(0)',
                },
              },
            }}
          >
            {message.sender === 'bot' && (
              <Avatar
                sx={{
                  bgcolor: 'rgba(var(--background-color), 0.9)',
                  width: 36,
                  height: 36,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  border: '2px solid #fff',
                  animation: 'fadeIn 0.3s ease-out 0.2s both',
                  '@keyframes fadeIn': {
                    '0%': {
                      opacity: 0,
                      transform: 'scale(0.8)',
                    },
                    '100%': {
                      opacity: 1,
                      transform: 'scale(1)',
                    },
                  },
                }}
              >
                <SmartToyIcon fontSize="small" />
              </Avatar>
            )}

            <Paper
              elevation={0}
              sx={{
                padding: '12px 16px',
                maxWidth: '85%',
                background: message.sender === 'user' 
                  ? 'linear-gradient(135deg, rgba(var(--background-color), 0.12) 0%, rgba(var(--background-color), 0.08) 100%)'
                  : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                borderRadius: message.sender === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                border: message.sender === 'user' 
                  ? '1px solid rgba(var(--background-color), 0.2)'
                  : '1px solid #e8e8e8',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                },
              }}
            >
              {message.sender === 'bot' ? (
                <>
                  {/* Răspunsul text formatat cu Markdown */}
                  {message.text && (
                    <div 
                      dangerouslySetInnerHTML={{ __html: formatMarkdown(message.text) }}
                      style={{ 
                        marginBottom: message.html ? '20px' : '0',
                        lineHeight: '1.6'
                      }}
                    />
                  )}
                  
                  {/* Sursele din manual (HTML cu accordion-uri) */}
                  {message.html && (
                    <Box sx={{ marginTop: '20px' }}>
                      <Box
                        sx={{
                          background: '#f0f0f0',
                          padding: '10px 15px',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          userSelect: 'none',
                          '&:hover': {
                            background: '#e5e5e5',
                          },
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          const content = e.currentTarget.nextElementSibling;
                          const icon = e.currentTarget.querySelector('.sources-icon');
                          if (content.style.display === 'none' || content.style.display === '') {
                            content.style.display = 'block';
                            icon.textContent = '▼';
                            
                            // Activează accordion-urile interne după ce se afișează conținutul
                            setTimeout(() => {
                              const moduleHeaders = content.querySelectorAll('.module-header');
                              moduleHeaders.forEach(header => {
                                if (!header.hasAttribute('data-listener-attached')) {
                                  header.setAttribute('data-listener-attached', 'true');
                                  header.addEventListener('click', function(e) {
                                    e.stopPropagation();
                                    const moduleId = this.getAttribute('data-module-id');
                                    const moduleContent = document.getElementById(moduleId);
                                    const moduleIcon = document.getElementById(moduleId + '_icon');
                                    
                                    if (moduleContent && moduleIcon) {
                                      if (moduleContent.style.display === 'none' || moduleContent.style.display === '') {
                                        moduleContent.style.display = 'block';
                                        moduleIcon.textContent = '▼';
                                      } else {
                                        moduleContent.style.display = 'none';
                                        moduleIcon.textContent = '▶';
                                      }
                                    }
                                  });
                                }
                              });
                            }, 100);
                          } else {
                            content.style.display = 'none';
                            icon.textContent = '▶';
                          }
                        }}
                      >
                        <Typography sx={{ fontSize: '14px', color: '#666', fontWeight: 600 }}>
                          📚 Vezi surse din manual
                        </Typography>
                        <span className="sources-icon" style={{ fontSize: '16px' }}>▶</span>
                      </Box>
                      <Box
                        sx={{ display: 'none', marginTop: '10px' }}
                        dangerouslySetInnerHTML={{ __html: message.html }}
                      />
                    </Box>
                  )}
                </>
              ) : (
                <div 
                  dangerouslySetInnerHTML={{ __html: formatMarkdown(message.text) }}
                  style={{ lineHeight: '1.6' }}
                />
              )}
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  marginTop: '4px',
                  color: 'text.secondary',
                  fontSize: '0.7rem',
                }}
              >
                {formatTime(message.timestamp)}
              </Typography>
            </Paper>

            {message.sender === 'user' && (
              <Avatar
                sx={{
                  bgcolor: 'rgba(var(--background-color), 0.9)',
                  width: 36,
                  height: 36,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  border: '2px solid #fff',
                  animation: 'fadeIn 0.3s ease-out 0.2s both',
                  '@keyframes fadeIn': {
                    '0%': {
                      opacity: 0,
                      transform: 'scale(0.8)',
                    },
                    '100%': {
                      opacity: 1,
                      transform: 'scale(1)',
                    },
                  },
                }}
              >
                <PersonIcon fontSize="small" />
              </Avatar>
            )}
          </Box>
        ))}

        {isLoading && (
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px',
              animation: 'slideInMessage 0.4s ease-out',
              '@keyframes slideInMessage': {
                '0%': {
                  opacity: 0,
                  transform: 'translateX(-20px)',
                },
                '100%': {
                  opacity: 1,
                  transform: 'translateX(0)',
                },
              },
            }}
          >
            <Avatar
              sx={{
                bgcolor: 'rgba(var(--background-color), 0.9)',
                width: 36,
                height: 36,
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                border: '2px solid #fff',
              }}
            >
              <SmartToyIcon fontSize="small" />
            </Avatar>
            <Paper
              elevation={0}
              sx={{
                padding: '12px 16px',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                borderRadius: '18px 18px 18px 4px',
                border: '1px solid #e8e8e8',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              }}
            >
  
              <CircularProgress size={20} sx={{ 
                animation: 'pulse 1.5s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': {
                    opacity: 1,
                  },
                  '50%': {
                    opacity: 0.5,
                  },
                },
              }} />
            </Paper>
          </Box>
        )}

        <div ref={messagesEndRef} />
      </Box>

      {/* Zona de input */}
      <Box
        sx={{
          padding: '20px',
          backgroundColor: '#fff',
          borderTop: '1px solid #e0e0e0',
        }}
      >
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center',
            backgroundColor: '#f8f9fa',
            borderRadius: '24px',
            padding: '8px 12px 8px 20px',
            border: '1px solid #e0e0e0',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: '#c0c0c0',
              backgroundColor: '#fff',
            },
            '&:focus-within': {
              borderColor: 'rgba(var(--background-color), 0.5)',
              backgroundColor: '#fff',
              boxShadow: '0 0 0 3px rgba(var(--background-color), 0.1)',
            },
          }}
        >
          <TextField
            fullWidth
            multiline
            maxRows={4}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Întreabă-mă orice despre JuristPRO NEXT..."
            variant="standard"
            disabled={isLoading}
            inputRef={inputRef}
            InputProps={{
              disableUnderline: true,
            }}
            sx={{
              '& .MuiInputBase-root': {
                backgroundColor: 'transparent',
                border: 'none',
                boxShadow: 'none',
                '&:before, &:after': {
                  display: 'none',
                },
              },
              '& .MuiInputBase-input': {
                padding: '8px 0',
                fontSize: '14px',
                lineHeight: '1.5',
                backgroundColor: 'transparent',
                border: 'none',
                boxShadow: 'none',
                '&:focus': {
                  backgroundColor: 'transparent',
                  border: 'none',
                  boxShadow: 'none',
                  outline: 'none',
                },
              },
              '& .MuiInputBase-input::placeholder': {
                color: '#999',
                opacity: 1,
              },
            }}
          />
          <IconButton
            color="primary"
            onClick={handleSendMessage}
            disabled={!inputMessage.trim() || isLoading}
            sx={{
              marginLeft: '8px',
              backgroundColor: 'rgba(var(--background-color), 0.9)',
              color: '#fff',
              width: '40px',
              height: '40px',
              '&:hover': {
                backgroundColor: 'rgba(var(--background-color), 1)',
                transform: 'scale(1.05)',
              },
              '&:disabled': {
                backgroundColor: '#e0e0e0',
                color: '#999',
              },
              transition: 'all 0.2s ease',
            }}
          >
            <SendIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Footer cu link către manual */}
      <Box
        sx={{
          padding: '12px 20px',
          backgroundColor: '#f8f9fa',
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
        }}
      >
        <HelpOutlineIcon sx={{ fontSize: '18px', color: '#666' }} />
        <Typography sx={{ fontSize: '13px', color: '#666' }}>
          Nu ai găsit răspunsul potrivit?{' '}
          <Link
            component="button"
            onClick={handleOpenManual}
            sx={{
              color: 'rgba(var(--background-color), 0.9)',
              textDecoration: 'none',
              fontWeight: 500,
              cursor: 'pointer',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            Consultă manualul de utilizare
          </Link>
        </Typography>
      </Box>

      {/* Dialog pentru manual */}
      <ManualUtilizare open={openManual} onClose={handleCloseManual} />
    </Box>
  );
};

export default AsistentChat;
